import { STAFF_SESSION_KEYS } from './staffSession';

/**
 * The invite token from an email link, parked across the Auth0 round trip.
 *
 * It has to be parked, and the reason is mechanical: the link is
 * `…/invites/accept#token=…`, Auth0 returns to the app's registered redirect URI (the origin) rather
 * than to the path it left from, and a fragment does not survive the hop in any case. So the token is
 * written before the redirect and read on the way back.
 *
 * `sessionStorage`, not `localStorage`: it belongs to this tab and this sign-in, and an invite left
 * lying around in a browser that shares a machine is exactly the thing not to leave lying around.
 * Synchronous, because App routing reads it during a render. Every access is wrapped — a private
 * window or blocked site data reads as "nothing parked", never as a crash.
 */
function store(): Storage | undefined {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
}

export const pendingInvite = {
  read(): string | null {
    try {
      return store()?.getItem(STAFF_SESSION_KEYS.pendingInvite) ?? null;
    } catch {
      return null;
    }
  },
  write(token: string): void {
    try {
      store()?.setItem(STAFF_SESSION_KEYS.pendingInvite, token);
    } catch {
      // ignore: the accept then only works without a sign-in hop, which is the common case
    }
  },
  clear(): void {
    try {
      store()?.removeItem(STAFF_SESSION_KEYS.pendingInvite);
    } catch {
      // ignore
    }
  },
};

/** `#token=…` off a link the API's email built, or null. Also accepts `?token=` for a hand-typed URL. */
export function inviteTokenFromUrl(url: { hash: string; search: string }): string | null {
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  return hash.get('token') ?? new URLSearchParams(url.search).get('token');
}
